import { Injectable } from '@angular/core';
import { ProductCart, ProductDetailsCart } from '@models';

@Injectable({
  providedIn: 'root',
})
export class CartRelationshipService {
  /**
   * Validates that a lineNumber is a valid positive number
   * @param lineNumber - The lineNumber to validate
   * @returns true if lineNumber is a valid positive number, false otherwise
   */
  isValidLineNumber(lineNumber: number | undefined | null): boolean {
    return lineNumber != null && typeof lineNumber === 'number' && lineNumber > 0;
  }

  //   recalculateRelatedLineNumbers(
  //   cart: ProductDetailsCart,
  //   appleCareProductIds: number[],
  //   onUpdate: (productId: number, newRelatedLineNumber: number) => void
  // ): void {
  //   const cartProducts = cart?.products ?? [];
  //   if (cartProducts.length === 0) return;

  //   const appleCareProductsInCart = cartProducts.filter(product =>
  //     appleCareProductIds.includes(product.productId) &&
  //     product.relatedLineNumber !== null &&
  //     product.relatedLineNumber !== undefined
  //   );

  //   const updatesNeeded: { productId: number; newRelatedLineNumber: number }[] = [];

  //   appleCareProductsInCart.forEach(appleCareProduct => {
  //     // Check if lineNumber equals relatedLineNumber (invalid state)
  //     if (appleCareProduct.lineNumber === appleCareProduct.relatedLineNumber) {

  //       // Find the correct parent
  //       const parentProduct = cartProducts.find(product =>
  //         appleCareProduct.relatedParentProducts?.some(parent => parent.productId === product.productId)
  //       );

  //       if (parentProduct) {
  //         updatesNeeded.push({
  //           productId: appleCareProduct.productId,
  //           newRelatedLineNumber: parentProduct.lineNumber
  //         });
  //       }
  //     } else {
  //       // Normal check: parent's lineNumber changed
  //       const parentProduct = cartProducts.find(product =>
  //         appleCareProduct.relatedParentProducts?.some(parent => parent.productId === product.productId)
  //       );

  //       if (parentProduct && parentProduct.lineNumber !== appleCareProduct.relatedLineNumber) {
  //         updatesNeeded.push({
  //           productId: appleCareProduct.productId,
  //           newRelatedLineNumber: parentProduct.lineNumber
  //         });
  //       }
  //     }
  //   });

  //   if (updatesNeeded.length > 0) {
  //     updatesNeeded.forEach(update => onUpdate(update.productId, update.newRelatedLineNumber));
  //   }
  // }

  validateAppleCareProducts(cart: ProductDetailsCart, appleCareProductIds: number[]): ProductCart[] {
    const cartProducts = cart?.products ?? [];
    const orphanedAppleCareItems: ProductCart[] = [];

    const appleCareProductsInCart = cartProducts.filter((product) => appleCareProductIds.includes(product.productId));

    appleCareProductsInCart.forEach((appleCareProduct) => {
      const parentProductExists = this.checkAppleCareHasParent(appleCareProduct, cartProducts);

      if (!parentProductExists) {
        orphanedAppleCareItems.push(appleCareProduct);
      }
    });

    return orphanedAppleCareItems;
  }

  /**
   * Validates that all AppleCare items in the cart have a valid relatedLineNumber
   * @param cart - The cart to validate
   * @param appleCareProductIds - Array of AppleCare product IDs
   * @returns Object with isValid flag and array of invalid AppleCare items
   */
  validateAppleCareRelatedLineNumbers(
    cart: ProductDetailsCart,
    appleCareProductIds: number[],
  ): { isValid: boolean; invalidItems: ProductCart[] } {
    const cartProducts = cart?.products ?? [];
    const invalidItems: ProductCart[] = [];

    const appleCareProductsInCart = cartProducts.filter((product) => appleCareProductIds.includes(product.productId));

    appleCareProductsInCart.forEach((appleCareProduct) => {
      // Check if relatedLineNumber is valid (not null, undefined, or invalid)
      if (!this.isValidLineNumber(appleCareProduct.relatedLineNumber)) {
        invalidItems.push(appleCareProduct);
      } else {
        // Also verify the parent product actually exists
        const parentExists = this.checkAppleCareHasParent(appleCareProduct, cartProducts);
        if (!parentExists) {
          invalidItems.push(appleCareProduct);
        }
      }
    });

    return {
      isValid: invalidItems.length === 0,
      invalidItems,
    };
  }

  private checkAppleCareHasParent(appleCareProduct: ProductCart, cartProducts: ProductCart[]): boolean {
    // Primary: Check by relatedLineNumber (1:1 line-based relationship)
    // If relatedLineNumber is explicitly null, the item is orphaned (no parent link)
    if (appleCareProduct.relatedLineNumber === null) {
      return false;
    }

    // If relatedLineNumber is defined and valid, use it for the check
    if (
      appleCareProduct.relatedLineNumber !== undefined &&
      this.isValidLineNumber(appleCareProduct.relatedLineNumber)
    ) {
      // Check if a product with that lineNumber exists in the cart
      return cartProducts.some((product) => product.lineNumber === appleCareProduct.relatedLineNumber);
    }

    // Fallback: Check by relatedParentProducts (legacy/type-based, only if relatedLineNumber is undefined)
    // Note: If relatedLineNumber was explicitly null, we already returned false above
    if (!appleCareProduct.relatedParentProducts || appleCareProduct.relatedParentProducts.length === 0) {
      return false;
    }

    return appleCareProduct.relatedParentProducts.some((parent) =>
      cartProducts.some((product) => product.productId === parent.productId),
    );
  }
}
